<template>
  <v-container fluid>
    <v-card flat :height="headerCardHeight" class="overflow-y-auto">
      <v-row no-gutters class="fill-height">
        <v-col cols="6" class="mt-8">
          <v-card-title class="text-h4">
            <v-icon large left color="primary lighten-3">
              mdi-google-analytics
            </v-icon>
            Kiosk Analytics - {{ getSelectedKioskAnalytics.name }}
          </v-card-title>
        </v-col>
      </v-row>
    </v-card>

    <v-card class="px-3 mt-3" flat>
      <v-tabs v-model="tab">
        <v-tab>
          <v-icon left>mdi-google-analytics</v-icon>
          Statistics
        </v-tab>

        <v-tab>
          <v-icon left>mdi-timer-check</v-icon>
          User Sessions
        </v-tab>

        <v-tab>
          <v-icon left>mdi-forum</v-icon>
          Q&A Analytics
        </v-tab>

        <v-spacer></v-spacer>

        <div class="font-weight-bold mt-4 mr-6">Date: {{ displayDate }}</div>

        <v-card flat width="240" class="d-flex align-center">
          <v-select
            :items="duration"
            outlined
            v-model="selectedDuration"
            dense
            class="mt-8"
            ref="durationSelector"
          >
            <template v-slot:item="data">
              <!-- Existing single date picker hook -->
              <v-list-item
                v-if="data.item === 'Select Date'"
                @click="
                  $store.commit('setSingleDatePicker', true);
                  selectedDuration = 'Select Date';
                  $refs.durationSelector.isMenuActive = false;
                "
              >
                Select Date
              </v-list-item>

              <!-- NEW: Range -->
              <v-list-item
                v-else-if="data.item === 'Select Range'"
                @click="openRangePicker()"
              >
                Select Range
              </v-list-item>

              <!-- NEW: Year -->
              <v-list-item
                v-else-if="data.item === 'Select Year'"
                @click="openYearPicker()"
              >
                Select Year
              </v-list-item>

              <!-- Default -->
              <v-list-item v-else @click="durationSetter(data.item)">
                {{ data.item }}
              </v-list-item>
            </template>
          </v-select>
        </v-card>
      </v-tabs>

      <!-- NEW: Range Picker Menu -->
      <v-menu
        v-model="rangeMenu"
        :close-on-content-click="false"
        offset-y
        max-width="320"
        min-width="320"
      >
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Select Date Range
          </v-card-title>

          <v-card-text class="pt-0">
            <v-date-picker v-model="rangeDates" range scrollable />
            <div class="text-caption mt-2">
              {{ rangeLabel }}
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelRange()">Cancel</v-btn>
            <v-btn color="primary" @click="applyRange()">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <!-- NEW: Year Picker Menu -->
      <v-menu
        v-model="yearMenu"
        :close-on-content-click="false"
        offset-x
        max-width="350"
        min-width="260"
      >
        <v-card>
          <v-card-title class="text-subtitle-1 font-weight-bold">
            Select Year
          </v-card-title>

          <v-card-text class="pt-0">
            <v-select
              outlined
              dense
              label="Year"
              :items="yearOptions"
              v-model="selectedYear"
            />
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="cancelYear()">Cancel</v-btn>
            <v-btn color="primary" @click="applyYear()">Apply</v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>

      <v-tabs-items v-model="tab" class="mt-1">
        <v-tab-item>
          <reports
            :userSessionList="userSessionList"
            :selectedDuration="selectedDuration"
          />
        </v-tab-item>

        <v-tab-item>
          <userSession />
        </v-tab-item>

        <v-tab-item>
          <qnaAnalytics :analysisList="analysisList" :loading="loading" />
        </v-tab-item>

        <v-tab-item>
          <TelepresenceAnalytics />
        </v-tab-item>

        <v-tab-item>
          <EmailAnalytics />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";
import userSession from "@/components/dashboard/admin/kioskAnalytics/userSession.vue";
import qnaAnalytics from "@/components/dashboard/admin/kioskAnalytics/qnaAnalytics.vue";
import Reports from "../../components/dashboard/admin/kioskAnalytics/reports.vue";
import TelepresenceAnalytics from "../../components/dashboard/admin/kioskAnalytics/telepresenceAnalytics.vue";
import EmailAnalytics from "../../components/dashboard/admin/kioskAnalytics/emailAnalytics.vue";

export default {
  name: "kioskAnalytics",
  components: {
    userSession,
    qnaAnalytics,
    Reports,
    TelepresenceAnalytics,
    EmailAnalytics,
  },
  data() {
    return {
      selectedKiosk: null,
      tab: null,
      loading: false,
      headerCardHeight: window.innerHeight / 8,

      // ✅ Added Current Year + Select Range + Select Year
      duration: [
        "Today",
        "Yesterday",
        "This Week",
        "Current Month",
        "Select Year",
        "Select Range"
      ],
      selectedDuration: "Today",

      // ✅ Range picker state
      rangeMenu: false,
      rangeDates: [], // expects ["YYYY-MM-DD","YYYY-MM-DD"]

      // ✅ Year picker state
      yearMenu: false,
      selectedYear: new Date().getFullYear(),
    };
  },
  computed: {
    ...mapGetters([
      "allRegisteredKiosks",
      "kioskAnalytics",
      "getWindowDimensions",
      "selectKioskAnalytics",
      "displayDate",
      "getSelectedKioskAnalytics"
    ]),
    kioskList() {
      let kioskName = [];
      this.allRegisteredKiosks.forEach((kiosk) => kioskName.push(kiosk.data().name));
      return kioskName;
    },
    getKioskName() {
      let temp;
      console.log("selected kiosk for analytics: ", this.selectKioskAnalytics)
      temp = this.allRegisteredKiosks.filter(
        (kiosk) => kiosk.id === this.selectKioskAnalytics
      );
      return temp[0].data().name;
    },
    analysisList() {
      let interactions = [];
      this.kioskAnalytics.qnaAnalytics.forEach((event) => {
        try {
          if (event.result.answers.length > 0) {
            interactions.push({
              kioskId: event.kioskID,
              result: event.result,
              timeStamp: event.timeStamp,
              transcript: event.transcript,
              id: event.id,
            });
          }
        } catch (err) {
          console.log(err)
        }
      });

      return interactions.filter(
        (interaction) => interaction.kioskId === this.selectKioskAnalytics
      );
    },
    userSessionList() {
      let sessions = [];
      this.kioskAnalytics.userSessionAnalytics.forEach((event) => {
        if (event.userSession.length > 2) {
          let startTime = moment(event.sessionStartTime);
          let endTime = moment(event.sessionEndTime);
          sessions.push({
            id: event.id,
            kioskId: event.kioskId,
            userSessionDate: event.sessionStartTime,
            userSessionDuration: endTime.diff(startTime),
            userSessionStartTime: event.sessionStartTime,
            userSessionEndTime: event.sessionEndTime,
            userSessionLogs: event.userSession,
          });
        }
      });
      return sessions.filter((session) => session.kioskId === this.selectKioskAnalytics);
    },

    // ✅ Range label for UI
    rangeLabel() {
      if (!this.rangeDates || this.rangeDates.length < 2) return "Pick a start and end date";
      const [start, end] = this.rangeDates;
      return `${start} → ${end}`;
    },

    // ✅ Year list (last 10 years, adjust if you want more)
    yearOptions() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current; y >= current - 10; y--) years.push(y);
      return years;
    },
  },
  watch: {
    selectedKiosk(newVal) {
      this.loadData(newVal);
    },
    getWindowDimensions(newVal) {
      this.headerCardHeight = newVal.height / 5.5;
    },
  },
  methods: {
    durationSetter(timeframe) {
      this.selectedDuration = timeframe;
      this.$refs.durationSelector.isMenuActive = false;

      if (timeframe === "Today") {
        this.$store.dispatch("getKioskAnalytics", { type: "Today", start: null, end: null });
      } else if (timeframe === "Yesterday") {
        this.$store.dispatch("getKioskAnalytics", { type: "Yesterday", start: null, end: null });
      } else if (timeframe === "This Week") {
        this.$store.dispatch("getKioskAnalytics", { type: "This Week", start: null, end: null });
      } else if (timeframe === "Current Month") {
        this.$store.dispatch("getKioskAnalytics", { type: "Current Month", start: null, end: null });
      } else if (timeframe === "Current Year") {
        this.$store.dispatch("getKioskAnalytics", { type: "Current Year", start: null, end: null });
      } else if (timeframe === null) {
        this.$store.dispatch("getKioskAnalytics", { type: "Today", start: null, end: null });
      }
    },

    // ✅ Open range picker menu
    openRangePicker() {
      this.selectedDuration = "Select Range";
      this.$refs.durationSelector.isMenuActive = false;
      this.rangeMenu = true;
    },
    cancelRange() {
      this.rangeMenu = false;
      // optional: reset range
      // this.rangeDates = [];
    },
    applyRange() {
      if (!this.rangeDates || this.rangeDates.length < 2) return;

      const [start, end] = this.rangeDates;

      this.rangeMenu = false;

      // dispatch with explicit start/end (ISO date strings)
      this.$store.dispatch("getKioskAnalytics", {
        type: "Select Year",
        start: start,
        end: end,
      });
    },

    // ✅ Open year picker menu
    openYearPicker() {
      this.selectedDuration = "Select Year";
      this.$refs.durationSelector.isMenuActive = false;
      this.yearMenu = true;
    },
    cancelYear() {
      this.yearMenu = false;
    },
    applyYear() {
      const y = Number(this.selectedYear);
      if (!y) return;

      const start = `${y}-01-01`;
      const end = `${y}-12-31`;

      this.yearMenu = false;

      this.$store.dispatch("getKioskAnalytics", {
        type: "Select Year",
        start: start,
        end: end,
      });
    },
  },
  mounted() {
    if (this.allRegisteredKiosks.length === 0) {
      this.$store.dispatch("getAllRegisteredKiosks");
    }
  },
};
</script>
